import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { formatMoney, pickLocale } from '../lib/format';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { BrandMark } from '../components/BrandMark';

type I18nText = Record<string, string>;
type Phase = 'init' | 'ready' | 'anon_disabled' | 'error';

interface Category { id: string; name: I18nText; position: number }
interface Item {
  id: string; category_id: string | null; name: I18nText; price_cents: number; position: number;
  schedule_enabled: boolean; schedule_days: number[]; schedule_start: string | null; schedule_end: string | null;
  is_featured: boolean; featured_label: I18nText; hide_when_off: boolean;
}

const DOW_MAP: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

function nowInTz(tz: string): { dow: number; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(new Date());
  const wd = parts.find((p) => p.type === 'weekday')?.value ?? 'Sun';
  let hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  if (hour === 24) hour = 0;
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  return { dow: DOW_MAP[wd] ?? 0, minutes: hour * 60 + minute };
}
const timeToMin = (t: string | null): number => {
  if (!t) return 0;
  const [h, m] = t.split(':');
  return Number(h) * 60 + Number(m);
};
// espelha public.item_available_now (fuso do restaurante, trata virada de meia-noite)
function itemAvailableNow(it: Item, tz: string): boolean {
  if (!it.schedule_enabled) return true;
  const days = it.schedule_days ?? [];
  if (!it.schedule_start || !it.schedule_end || days.length === 0) return true;
  const { dow, minutes } = nowInTz(tz);
  const prev = (dow + 6) % 7;
  const start = timeToMin(it.schedule_start);
  const end = timeToMin(it.schedule_end);
  if (start <= end) return days.includes(dow) && minutes >= start && minutes < end;
  return (days.includes(dow) && minutes >= start) || (days.includes(prev) && minutes < end);
}
interface CartLine { qty: number; notes: string }
interface MyItem {
  id: string;
  name_snapshot: string;
  quantity: number;
  unit_price_cents: number;
  kitchen_status: string;
  orders: { approval_status: string } | null;
}

function statusKey(it: MyItem): string {
  if (it.kitchen_status === 'cancelled') return 'status_rejected';
  if (it.kitchen_status === 'in_prep') return 'status_in_prep';
  if (it.kitchen_status === 'ready') return 'status_ready';
  if (it.kitchen_status === 'served') return 'status_served';
  return 'status_pending';
}

export function DinerPage() {
  const { qrToken } = useParams<{ qrToken: string }>();
  const { t } = useTranslation();

  const [phase, setPhase] = useState<Phase>('init');
  const [restaurant, setRestaurant] = useState<{ name: string; locale: string; currency: string; timezone: string } | null>(null);
  const [, setTick] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [cart, setCart] = useState<Record<string, CartLine>>({});
  const [myItems, setMyItems] = useState<MyItem[]>([]);
  const [sending, setSending] = useState(false);
  const tabIdRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const restIdRef = useRef<string | null>(null);
  const [isAnon, setIsAnon] = useState(false);
  const [closed, setClosed] = useState(false);
  const [npsScore, setNpsScore] = useState(10);
  const [npsSent, setNpsSent] = useState(false);
  const [points, setPoints] = useState<number | null>(null);
  const [threshold, setThreshold] = useState<number | null>(null);
  const [redeemCode, setRedeemCode] = useState<string | null>(null);
  const [billRequested, setBillRequested] = useState(false);

  const loadMyItems = useCallback(async () => {
    const tabId = tabIdRef.current;
    if (!tabId) return;
    const { data } = await supabase
      .from('order_items')
      .select('id, name_snapshot, quantity, unit_price_cents, kitchen_status, orders(approval_status)')
      .eq('tab_id', tabId)
      .order('created_at');
    if (data) setMyItems(data as unknown as MyItem[]);
  }, []);

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;

    async function start() {
      if (!qrToken) return setPhase('error');
      // sessão (cria anônima se necessário)
      const { data: s } = await supabase.auth.getSession();
      if (!s.session) {
        const { error: anonErr } = await supabase.auth.signInAnonymously();
        if (anonErr) return setPhase('anon_disabled');
      }
      // abre/entra na comanda
      const { data: opened, error: openErr } = await supabase.rpc('diner_open_tab', { p_qr_token: qrToken });
      if (openErr || !opened) return setPhase('error');
      const info = opened as { tab_id: string; session_id: string; restaurant_id: string };
      tabIdRef.current = info.tab_id;
      sessionIdRef.current = info.session_id;
      restIdRef.current = info.restaurant_id;
      const { data: u } = await supabase.auth.getUser();
      setIsAnon(Boolean(u.user?.is_anonymous));

      const { data: r } = await supabase
        .from('restaurants')
        .select('name, locale, currency, timezone')
        .eq('id', info.restaurant_id)
        .single();
      const { data: cats } = await supabase
        .from('menu_categories')
        .select('id, name, position')
        .eq('restaurant_id', info.restaurant_id)
        .eq('is_active', true)
        .order('position');
      const { data: its } = await supabase
        .from('menu_items')
        .select('id, category_id, name, price_cents, position, schedule_enabled, schedule_days, schedule_start, schedule_end, is_featured, featured_label, hide_when_off')
        .eq('restaurant_id', info.restaurant_id)
        .eq('is_available', true)
        .order('position');

      if (r) setRestaurant(r as { name: string; locale: string; currency: string; timezone: string });
      if (cats) setCategories(cats as Category[]);
      if (its) setItems(its as Item[]);
      await loadMyItems();

      // fidelidade (programa público + saldo próprio)
      const { data: prog } = await supabase
        .from('loyalty_programs')
        .select('is_active, reward_threshold')
        .eq('restaurant_id', info.restaurant_id)
        .maybeSingle();
      if (prog?.is_active) {
        setThreshold(prog.reward_threshold as number);
        const { data: acc } = await supabase
          .from('loyalty_accounts')
          .select('points_balance')
          .eq('restaurant_id', info.restaurant_id)
          .maybeSingle();
        setPoints((acc?.points_balance as number) ?? 0);
      }

      setPhase('ready');

      channel = supabase
        .channel(`tab-${info.tab_id}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'order_items', filter: `tab_id=eq.${info.tab_id}` }, () => void loadMyItems())
        .on('postgres_changes', { event: '*', schema: 'public', table: 'orders', filter: `tab_id=eq.${info.tab_id}` }, () => void loadMyItems())
        .on('postgres_changes', { event: '*', schema: 'public', table: 'table_sessions', filter: `id=eq.${info.session_id}` }, (payload) => {
          if ((payload.new as { status?: string })?.status === 'closed') setClosed(true);
        })
        .subscribe();
    }

    void start();
    return () => {
      if (channel) void supabase.removeChannel(channel);
    };
  }, [qrToken, loadMyItems]);

  const locale = restaurant?.locale ?? 'pt-BR';
  const currency = restaurant?.currency ?? 'BRL';
  const tz = restaurant?.timezone ?? 'America/Sao_Paulo';

  // reavalia janelas de horário sem precisar recarregar
  useEffect(() => {
    const id = window.setInterval(() => setTick((x) => x + 1), 30000);
    return () => window.clearInterval(id);
  }, []);

  function addToCart(item: Item) {
    setCart((prev) => ({ ...prev, [item.id]: { qty: (prev[item.id]?.qty ?? 0) + 1, notes: prev[item.id]?.notes ?? '' } }));
  }
  function setQty(id: string, qty: number) {
    setCart((prev) => {
      const next = { ...prev };
      if (qty <= 0) delete next[id];
      else next[id] = { qty, notes: prev[id]?.notes ?? '' };
      return next;
    });
  }
  function setNotes(id: string, notes: string) {
    setCart((prev) => ({ ...prev, [id]: { qty: prev[id]?.qty ?? 1, notes } }));
  }

  async function sendOrder() {
    const tabId = tabIdRef.current;
    const lines = Object.entries(cart);
    if (!tabId || lines.length === 0) return;
    setSending(true);
    const payload = lines.map(([menu_item_id, line]) => ({ menu_item_id, quantity: line.qty, notes: line.notes }));
    const { error } = await supabase.rpc('diner_place_order', { p_tab_id: tabId, p_items: payload });
    setSending(false);
    if (!error) {
      setCart({});
      void loadMyItems();
    }
  }

  async function redeem() {
    const rid = restIdRef.current;
    if (!rid) return;
    const { data, error } = await supabase.rpc('redeem_reward', { p_restaurant_id: rid });
    if (!error && data) {
      setRedeemCode((data as { code: string }).code);
      setPoints((p) => (p != null && threshold != null ? p - threshold : p));
    }
  }

  async function requestBill() {
    const tabId = tabIdRef.current;
    if (!tabId) return;
    await supabase.rpc('request_bill', { p_tab_id: tabId });
    setBillRequested(true);
  }

  async function sendNps() {
    const sid = sessionIdRef.current;
    if (!sid) return;
    await supabase.rpc('submit_feedback', { p_session_id: sid, p_score: npsScore, p_comment: null });
    setNpsSent(true);
  }

  const cartCount = Object.values(cart).reduce((sum, l) => sum + l.qty, 0);
  const itemName = (id: string) => items.find((i) => i.id === id)?.name;
  const total = myItems
    .filter((it) => it.kitchen_status !== 'cancelled')
    .reduce((sum, it) => sum + it.unit_price_cents * it.quantity, 0);

  if (phase === 'init') return <div className="center-note">{t('common.loading')}</div>;
  if (phase === 'anon_disabled') return <div className="center-note">{t('diner.anon_disabled')}</div>;
  if (phase === 'error') return <div className="center-note">{t('home.supabase_error')}</div>;

  return (
    <div className="diner">
      <header className="diner__header">
        <span className="brand">
          <BrandMark className="brand__mark" />
          <span className="brand__name">{restaurant?.name}</span>
        </span>
        <LanguageSwitcher />
      </header>

      <main className="diner__main">
        <h2 className="section-title">{t('diner.menu')}</h2>
        {categories.map((c) => {
          const list = items.filter((i) => i.category_id === c.id && !(i.hide_when_off && !itemAvailableNow(i, tz)));
          if (list.length === 0) return null;
          return (
            <div key={c.id} className="menu-cat">
              <h3 className="menu-cat__title">{pickLocale(c.name, locale)}</h3>
              {list.map((it) => {
                const avail = itemAvailableNow(it, tz);
                const promo = it.is_featured ? (pickLocale(it.featured_label, locale) || t('diner.promo')) : null;
                return (
                  <div key={it.id} className={`menu-line${avail ? '' : ' menu-line--off'}`}>
                    <span>
                      {promo && <em className="promo-badge">🕒 {promo}</em>}
                      {pickLocale(it.name, locale)} · {formatMoney(it.price_cents, currency, locale)}
                      {!avail && <em className="tag"> ({t('diner.unavailable_now')})</em>}
                    </span>
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => addToCart(it)} disabled={!avail}>
                      {t('diner.add')}
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}

        {cartCount > 0 && (
          <div className="cart">
            <h2 className="section-title">{t('diner.cart')}</h2>
            {Object.entries(cart).map(([id, line]) => (
              <div key={id} className="cart-line">
                <span className="cart-line__name">{pickLocale(itemName(id), locale)}</span>
                <div className="cart-line__controls">
                  <button type="button" onClick={() => setQty(id, line.qty - 1)}>−</button>
                  <span>{line.qty}</span>
                  <button type="button" onClick={() => setQty(id, line.qty + 1)}>+</button>
                </div>
                <input
                  className="cart-line__notes"
                  placeholder={t('diner.notes_ph')}
                  value={line.notes}
                  onChange={(e) => setNotes(id, e.target.value)}
                />
              </div>
            ))}
            <button type="button" className="btn btn--primary btn--block" onClick={() => void sendOrder()} disabled={sending}>
              {sending ? t('diner.sending') : t('diner.send_order')}
            </button>
          </div>
        )}

        {myItems.length > 0 && (
          <div className="my-account">
            <h2 className="section-title">{t('diner.my_account')}</h2>
            <ul className="list">
              {myItems.map((it) => (
                <li key={it.id} className="list__row">
                  <span>
                    {it.quantity}× {it.name_snapshot}
                  </span>
                  <span className="badge">{t(`diner.${statusKey(it)}`)}</span>
                </li>
              ))}
            </ul>
            <div className="total-row">
              <strong>{t('diner.total')}</strong>
              <strong>{formatMoney(total, currency, locale)}</strong>
            </div>
            {!closed && (
              billRequested ? (
                <p className="form-info" style={{ marginTop: 12 }}>{t('diner.bill_requested')}</p>
              ) : (
                <button type="button" className="btn btn--ghost btn--block" style={{ marginTop: 12 }} onClick={() => void requestBill()}>
                  {t('diner.request_bill')}
                </button>
              )
            )}
          </div>
        )}

        {!isAnon && points != null && threshold != null && (
          <div className="loyalty-box">
            <span>
              {t('diner.loyalty_points')}: <strong>{points}</strong> / {threshold}
            </span>
            {points >= threshold && !redeemCode && (
              <button type="button" className="btn btn--primary btn--sm" onClick={() => void redeem()}>
                {t('diner.redeem')}
              </button>
            )}
            {redeemCode && (
              <div className="code-box">
                {t('diner.your_code')}: <strong>{redeemCode}</strong>
              </div>
            )}
          </div>
        )}

        {closed && (
          <div className="nps-box">
            {npsSent ? (
              <p className="form-info">{t('diner.thanks_rating')}</p>
            ) : (
              <>
                <h2 className="section-title">{t('diner.closed_title')}</h2>
                <p>{t('diner.rate')}</p>
                <div className="form-row" style={{ alignItems: 'center' }}>
                  <input type="range" min={0} max={10} value={npsScore} onChange={(e) => setNpsScore(Number(e.target.value))} style={{ flex: 1 }} />
                  <strong style={{ width: 24, textAlign: 'center' }}>{npsScore}</strong>
                </div>
                <button type="button" className="btn btn--primary btn--block" onClick={() => void sendNps()}>
                  {t('diner.send_rating')}
                </button>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
